import React, { useEffect, useMemo } from 'react';
import {
  KiteCombobox,
  KiteComboboxListItem,
  boldSubstring,
  KiteChipGroup,
  KiteChip,
  useComboboxBehavior,
} from '@kite/react-kite';
import './FilterDropdownWithChips.scss';

// Represents a selected item object, e.g. { collateralId: "..." }
export interface ISelectedItem {
  [key: string]: string;
}

export interface IFilterDropdownWithChipsProps {
  data: unknown[];
  searchKey: string;
  displayKey: string;
  label: string;
  selectedItems: ISelectedItem[];
  options?: string[];
  onSelectionChange: (selections: ISelectedItem[]) => void;
  // onClearFilters: () => void;
  // onCancel: () => void;
  // onApplyFilters: () => void;
  onRemoveSelection: (value: ISelectedItem) => void;
}

const FilterDropdownWithChips: React.FC<IFilterDropdownWithChipsProps> = ({
  data,
  searchKey,
  displayKey,
  label,
  selectedItems,
  options,
  onSelectionChange,
  // onClearFilters,
  // onCancel,
  // onApplyFilters,
  onRemoveSelection,
}) => {
  const uniqueData: ISelectedItem[] = useMemo(() => {
    // If explicit options are provided from the API, use them directly
    if (options && options.length > 0) {
      return options.map((value) => ({ [searchKey]: value }));
    }

    // Fallback: derive options from the data prop
    const values = data.flatMap((item) => {
      const fieldValue = (item as Record<string, unknown>)[searchKey];

      if (Array.isArray(fieldValue)) {
        return fieldValue;
      }

      return fieldValue ? [String(fieldValue)] : [];
    });

    return Array.from(new Set(values)).map((value) => ({
      [searchKey]: String(value),
    }));
  }, [data, searchKey, options]);

  const {
    value,
    setInitialData,
    filteredSelections,
    searchHandler,
    setSelections,
    toggleAllHandler,
    resultsWithoutSelections,
  } = useComboboxBehavior({ searchKey });

  useEffect(() => {
    setInitialData(uniqueData);
  }, [uniqueData, setInitialData]);

  useEffect(() => {
    setSelections(selectedItems || []);
  }, [selectedItems, setSelections]);

  return (
    <div className="text-area-example">
      <KiteCombobox
        className="kite-combobox-orders"
        displayKey={displayKey}
        id={`${displayKey}-combobox`}
        labels={{ inputLabel: label, placeholder: 'Select any that apply' }}
        value={value}
        hasSearch={true}
        selections={selectedItems || []}
        onSearch={searchHandler}
        toggleAll={toggleAllHandler}
        onSelectionChange={(newSelections) =>
          onSelectionChange(newSelections as ISelectedItem[])
        }
      >
        {filteredSelections.map((searchResult: ISelectedItem, i: number) => (
          <KiteComboboxListItem
            key={searchResult[displayKey] + '-' + i}
            selected={true}
            value={searchResult}
          >
            {searchResult[displayKey]}
          </KiteComboboxListItem>
        ))}
        {resultsWithoutSelections.map(
          (searchResult: ISelectedItem, i: number) => (
            <KiteComboboxListItem
              key={searchResult[displayKey] + '-' + i}
              selected={false}
              value={searchResult}
            >
              {boldSubstring(searchResult[displayKey] as string, value)}
            </KiteComboboxListItem>
          )
        )}
      </KiteCombobox>
      <div className="active-filters-chips">
        <KiteChipGroup>
          {(selectedItems || []).map((value: ISelectedItem, index: number) => (
            <KiteChip
              key={value[displayKey] + '-' + index}
              label={value[displayKey]}
              selected={true}
              value={value}
              onClick={() => onRemoveSelection(value)}
              dismissible={true}
            />
          ))}
        </KiteChipGroup>
      </div>
    </div>
  );
};
export default FilterDropdownWithChips;
